import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuList,
  MenuItem,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IosShareIcon from '@mui/icons-material/IosShare';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useRetroActions } from './RetroActionsContext';
import { useAlert } from '../../contexts/alert/useAlert';
import { RetroCategory, RetroAction } from './analytics/RetroTracking';
import { useTracking } from '../../analytics/useTracking';

// ── Nested menu item ────────────────────────────────────────────────────────

interface NestedMenuItemProps {
  label: string;
  icon?: ReactElement;
  parentOpen: boolean;
  children: ReactNode;
}

/**
 * A MenuItem that opens a secondary Menu to its right on hover.
 * Uses a short debounce so the user can move the cursor diagonally to the
 * submenu without it flickering closed.
 */
function NestedMenuItem({ label, icon, parentOpen, children }: NestedMenuItemProps): ReactElement {
  const [open, setOpen] = useState(false);
  const itemRef = useRef<HTMLLIElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close submenu when the parent closes
  useEffect(() => {
    if (!parentOpen) setOpen(false);
  }, [parentOpen]);

  const scheduleOpen = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 80);
  };

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  return (
    <>
      <MenuItem
        ref={itemRef}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        selected={open}
        sx={{ justifyContent: 'space-between', minWidth: 160 }}
      >
        {icon && <ListItemIcon>{icon}</ListItemIcon>}
        <ListItemText>{label}</ListItemText>
        <ChevronRightIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
      </MenuItem>

      {/*
       * Popper with the built-in @popperjs/core `flip` modifier:
       * opens to the right of the anchor by default; automatically switches to
       * the left when the viewport has insufficient room on the right.
       */}
      <Popper
        open={open && parentOpen}
        anchorEl={itemRef.current}
        placement="right-start"
        modifiers={[{ name: 'flip', options: { fallbackPlacements: ['left-start'] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <Paper
          elevation={8}
          onMouseEnter={cancelClose}
          onMouseLeave={() => setOpen(false)}
        >
          <MenuList>
            {children}
          </MenuList>
        </Paper>
      </Popper>
    </>
  );
}

// ── Keyboard shortcut label ─────────────────────────────────────────────────

function Shortcut({ keys }: { keys: string }): ReactElement {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
      {keys}
    </Typography>
  );
}

// ── Main RetroMenu ──────────────────────────────────────────────────────────

/**
 * App-bar menu for the Retrospective board. Only rendered when the user is
 * inside a retro room (see UserApp.tsx).
 */
export function RetroMenu(): ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const {
    canUndo, canRedo, onUndo, onRedo,
    onExportMarkdown, onExportPlainText,
    canExportSelection, onExportSelectionMarkdown, onExportSelectionPlainText,
    onExportBoardAsImage, onExportSelectionAsImage,
    canEndRetro, onEndRetro,
    canReopenRetro, onReopenRetro,
  } = useRetroActions();
  const alert = useAlert();
  const { track } = useTracking();
  const isMac = navigator.platform.toUpperCase().includes('MAC');

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    track.event(RetroCategory.Room, RetroAction.BoardMenuOpened);
  };
  const handleClose = () => setAnchorEl(null);

  const handleInvite = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      alert.showMessage('Room link copied — share it with your team', 'success');
      track.event(RetroCategory.Room, RetroAction.InviteLinkCopied);
    }).catch(() => {
      alert.showMessage('Could not copy room link', 'error');
    });
    handleClose();
  };

  const handleUndo = () => {
    onUndo();
    handleClose();
  };

  const handleRedo = () => {
    onRedo();
    handleClose();
  };

  const handleExportMarkdown = () => {
    onExportMarkdown();
    handleClose();
  };

  const handleExportPlainText = () => {
    onExportPlainText();
    handleClose();
  };

  const handleExportSelectionMarkdown = () => {
    onExportSelectionMarkdown();
    handleClose();
  };

  const handleExportSelectionPlainText = () => {
    onExportSelectionPlainText();
    handleClose();
  };

  const handleExportBoardAsImage = () => {
    onExportBoardAsImage();
    handleClose();
  };

  const handleExportSelectionAsImage = () => {
    onExportSelectionAsImage();
    handleClose();
  };

  const handleEndRetro = () => {
    handleClose();
    const confirmed = window.confirm(
      'End this retro? It will be locked so no new participants can join. You and other signed-in participants can still reopen it.',
    );
    if (confirmed) {
      onEndRetro();
    }
  };

  const handleReopenRetro = () => {
    handleClose();
    const confirmed = window.confirm(
      'Reopen this retro? It will accept new participants again.',
    );
    if (confirmed) {
      onReopenRetro();
    }
  };

  return (
    <>
      <Tooltip title="Board menu">
        <IconButton
          color="inherit"
          onClick={handleOpen}
          aria-label="Board menu"
          size="small"
          sx={{ mr: 1 }}
        >
          <MoreVertIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MenuItem onClick={handleInvite}>
          <ListItemIcon><PersonAddIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Invite teammates</ListItemText>
        </MenuItem>

        <Divider />

        <NestedMenuItem
          label="Export"
          icon={<IosShareIcon fontSize="small" />}
          parentOpen={open}
        >
          <MenuItem onClick={handleExportMarkdown}>
            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Copy all as Markdown</ListItemText>
            <Shortcut keys={isMac ? '⌘⇧M' : 'Ctrl+Shift+M'} />
          </MenuItem>
          <MenuItem onClick={handleExportPlainText}>
            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Copy all as plain text</ListItemText>
            <Shortcut keys={isMac ? '⌘⇧C' : 'Ctrl+Shift+C'} />
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleExportSelectionMarkdown} disabled={!canExportSelection}>
            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Copy selection as Markdown</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportSelectionPlainText} disabled={!canExportSelection}>
            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Copy selection as plain text</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleExportBoardAsImage}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download board as PNG</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportSelectionAsImage} disabled={!canExportSelection}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download selection as PNG</ListItemText>
          </MenuItem>
        </NestedMenuItem>

        <NestedMenuItem
          label="Edit"
          icon={<EditIcon fontSize="small" />}
          parentOpen={open}
        >
          <MenuItem onClick={handleUndo} disabled={!canUndo}>
            <ListItemIcon><UndoIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Undo</ListItemText>
            <Shortcut keys={isMac ? '⌘Z' : 'Ctrl+Z'} />
          </MenuItem>
          <MenuItem onClick={handleRedo} disabled={!canRedo}>
            <ListItemIcon><RedoIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Redo</ListItemText>
            <Shortcut keys={isMac ? '⌘⇧Z' : 'Ctrl+Shift+Z'} />
          </MenuItem>
        </NestedMenuItem>

        {(canEndRetro || canReopenRetro) && [
          <Divider key="retro-state-divider" />,
          canEndRetro
            ? <MenuItem key="end-retro" onClick={handleEndRetro}>
                <ListItemIcon><LockIcon fontSize="small" /></ListItemIcon>
                <ListItemText>End retro</ListItemText>
              </MenuItem>
            : <MenuItem key="reopen-retro" onClick={handleReopenRetro}>
                <ListItemIcon><LockOpenIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Reopen retro</ListItemText>
              </MenuItem>,
        ]}
      </Menu>
    </>
  );
}
